/*---------- Deps, Hooks & CSS ----------*/
import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import styles from "./EventBadgesStatusManager.module.css"

/*---------- Services ----------*/
import * as progressService from '../../../services/progressService'
import * as clientEventService from '../../../services/clientEventService'

/*---------- Components ----------*/
import Card from "../../../components/Home/StatusSubpages/Card"
import LoadingSpinner from '../../../components/Miscellaneous/LoadingSpinner'
import ProgressSteps from '../../../components/Miscellaneous/ProgressSteps'

/*---------- Images ----------*/
import BadgeMapping from "../../../components/Home/StatusSubpages/EventBadgesImages/BadgeMapping.png"
import TemplateManagement from "../../../components/Home/StatusSubpages/ExtraBadgesImages/ExtraBadgeTemplateStyling.png"
import RegTypeMapping from "../../../components/Home/StatusSubpages/EventBadgesImages/RegTypeMapping.png"
import QRCode from "../../../components/Home/StatusSubpages/EventBadgesImages/QRCode.png"
import BadgeImageManager from "../../../components/Home/StatusSubpages/EventBadgesImages/BadgeImageManager.png"
import FieldOrdering from "../../../components/Home/StatusSubpages/EventBadgesImages/FieldOrdering.png"

const EventBadgesStatusManager = () => {

    const location = useLocation()
    const passedState = location.state
    const [clientEventInfo, setClientEventInfo] = useState({})
    const clientEventInfoRef = useRef(null);
    const [changeMade, setChangeMade] = useState(false)
    if (clientEventInfoRef === null || clientEventInfoRef.current === null) {
        if (!passedState || !passedState.clientName) {
            clientEventInfoRef.current = clientEventService.getClientEvent();
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            clientEventInfoRef.current = passedData
        }
    }
    useEffect(() => {
        // run events fetch every x seconds, if its too fast, it won't run - gets stuck
        const interval = setInterval(() => checkClientEvent(), (120 * 1000))
        return () => clearInterval(interval)
    }, [])
    const checkClientEvent = () => {
        // Update clientevent with every render when its value has changed.
        let savedClientEvent = clientEventService.getClientEvent()
        if (clientEventInfoRef.current && clientEventInfoRef.current.eventId !== undefined &&
            savedClientEvent && savedClientEvent.eventId !== undefined &&
            savedClientEvent.eventId !== clientEventInfoRef.current.eventId) {
            setClientEventInfo(savedClientEvent)
            clientEventInfoRef.current = savedClientEvent;
            setChangeMade(true)
        }
    }
    useEffect(() => {
        if (changeMade && clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined
            && clientEventInfo.eventId !== undefined && clientEventInfo.eventName !== undefined) {
            setChangeMade(false)
            fetchData()
        }

    }, [clientEventInfo])    
    /*---------- State ----------*/
    const [client, setClient] = useState(
        clientEventInfo.client
            ? clientEventInfo.client
            : clientEventInfo.clientId
                ? clientEventInfo.clientId
                : ""
    ) 
    const [event, setEvent] = useState(
        clientEventInfo.event
            ? clientEventInfo.event
            : clientEventInfo.eventId
                ? clientEventInfo.eventId
                : ""
    ) 
    const [clientEventObject, setClientEventObject] = useState({})
    const [loaded, setLoaded] = useState(false)

    // Required Setup
    const [eventBadgeMappingProgress, setEventBadgeMappingProgress] = useState("Loading...")
    const [regTypeMappingProgress, setRegTypeMappingProgress] = useState("Loading...")
    const [templateProgress, setTemplateProgress] = useState("Loading...")
    const [scannerImageProgress, setScannerImageProgress] = useState("Loading...")

    // Optional Setup
    const [imageProgress, setImageProgress] = useState("Loading...")
    const [fieldOrderingProgress, setFieldOrderingProgress] = useState("Loading...")

    // Progress Steps
    const [requiredCompletionCount, setRequiredCompletionCount] = useState(1)
    const [optionalCompletionCount, setOptionalCompletionCount] = useState(1)

    /*---------- Constants ----------*/
    // General
    const clientName = clientEventInfo && clientEventInfo.clientName
        ? clientEventInfo.clientName
        : ""

    const eventName = clientEventInfo && clientEventInfo.eventName
            ? clientEventInfo.eventName
            : ""
            
    // curently does nothing, do we need it?
    const ERSName = clientEventInfo && clientEventInfo.eventRegistrationSystem !== undefined
        ? clientEventInfo.eventRegistrationSystem
        : "ERS"

    const clientId = clientEventInfo && clientEventInfo.clientId
        ? clientEventInfo.clientId
        : ""

    const eventId = clientEventInfo &&  clientEventInfo.eventId
            ? clientEventInfo.eventId
            : ""

    // Progress Steps 
    const requiredSteps = [
        { label: 'Event Badge Mapping', step: 1 },
        { label: 'Registration Type Mapping', step: 2 },
        { label: 'Template Styling', step: 3 },
    ]

    const optionalSteps = [
        { label: 'QR Code Setup', step: 1 },
        { label: 'Image Upload', step: 2 },
        { label: 'Field Ordering', step: 3 },
    ]

    /*---------- Functions ----------*/
    // Step Calculations
    const checkRequiredCompletion = () => {
        if (eventBadgeMappingProgress === "Success") setRequiredCompletionCount(count => count + 1)
        if (regTypeMappingProgress === "Success") setRequiredCompletionCount(count => count + 1)
        if (templateProgress === "Success") setRequiredCompletionCount(count => count + 1)
    }

    const checkOptionalCompletion = () => {
        if (scannerImageProgress === "Success") setOptionalCompletionCount(count => count + 1)
        if (imageProgress === "Success") setOptionalCompletionCount(count => count + 1)
        if (fieldOrderingProgress === "Success") setOptionalCompletionCount(count => count + 1)
    }

    const checkLoaded = () => {
        if (eventBadgeMappingProgress !== "Loading..."
            && regTypeMappingProgress !== "Loading..."
            && templateProgress !== "Loading..."
            && scannerImageProgress !== "Loading..."
            && imageProgress !== "Loading..."
            && fieldOrderingProgress !== "Loading..."
        )
            setLoaded(true)
    }
    const fetchData = async () => {
        const clientEventObj = {
            ClientId: clientId,
            EventId: eventId,
        }
        setClientEventObject(clientEventObj)

        const [
            eventBadgeProgress,
            regTypeProgress,
            templateManagementProgress,
            qrProgress,
            imageProgressStatus,
            fieldProgress,
        ] = await Promise.all([
            progressService.getEventBadgesMappingProgress(clientEventObj),
            progressService.getRegistrationTypeMappingProgress(clientEventObj),
            progressService.getTemplateManagmentProgress(clientEventObj),
            progressService.getQrCodeLogicProgress(clientEventObj),
            progressService.getImageProgress(clientEventObj),
            progressService.getFieldOrderingProgress(clientEventObj),
        ])

        setEventBadgeMappingProgress(eventBadgeProgress)
        setRegTypeMappingProgress(regTypeProgress)
        setTemplateProgress(templateManagementProgress)
        setScannerImageProgress(qrProgress)
        setImageProgress(imageProgressStatus)
        setFieldOrderingProgress(fieldProgress)
    }
    useEffect(() => {
        if (clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined) {
            fetchData()
        }
    }, [clientEventInfo])
    useEffect(() => {
        // store into local object
        if (!passedState || !passedState.clientName) {
            //let localclientevent = clientEventService.getClientEvent()
            if (clientEventInfoRef.current) {
                setClientEventInfo(clientEventInfoRef.current)
            }
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            setClientEventInfo(passedData)
            clientEventInfoRef.current = passedData;
        }
    }, [])

    useEffect(() => {
        checkLoaded()
        checkRequiredCompletion()
        checkOptionalCompletion()
    }, [fieldOrderingProgress])
    
    return (
        <>
        {
                loaded ?                
                    <div id={styles.statusSubpage}>
                        <div className={styles.requiredSetupHeader}>
                            <h2 className={styles.requiredSetupHeading}>Required Setup</h2>
                            <ProgressSteps
                                steps={requiredSteps}
                                activeStep={requiredCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={BadgeMapping}
                                header="Event Badge Mapping"
                                content={`Map fields from ${ERSName} for ${eventName} badges`}
                                styles={styles}
                                linkName={`/BadgeToERSPropertyMapping`}
                                linkState={clientEventInfo}
                                disabledBool={false} // hardcoded as it's the first required step of an event --- can't be disabled
                            />
                            <Card
                                dataImage={RegTypeMapping}
                                header="Registration Type Mapping"
                                content={`Map registration types from ${ERSName} to desired fields/option selections, set field names, and additional parameters`}
                                styles={styles}
                                linkName={`/RegTypeMapping`}
                                linkState={clientEventInfo}
                                disabledBool={eventBadgeMappingProgress !== "Success" ? true : false}
                            />
                            <Card
                                dataImage={TemplateManagement}
                                header="Template Styling"
                                content={`Create styling rules & test printing`}
                                styles={styles}
                                linkName={`/MainBadgeTemplateStyling`}
                                linkState={clientEventInfo}
                                disabledBool={templateProgress !== "Success" ? true : false}
                            />
                        </div>

                        <div className={styles.optionalSetupHeader}>
                            <h2 className={styles.optionalSetupHeading}>Optional Setup</h2>
                            <ProgressSteps
                                steps={optionalSteps}
                                activeStep={optionalCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={QRCode}
                                header="QR Code Setup"
                                content={`Manage QR Code configuration for ${eventName}`}
                                styles={styles}
                                linkName={`/BadgeScannerImageManager`}
                                linkState={clientEventInfo}
                                disabledBool={regTypeMappingProgress !== "Success" ? true : false}
                            />
                            <Card
                                dataImage={BadgeImageManager}
                                header="Image Upload"
                                content={`Upload images/icons to be displayed on badges`}
                                styles={styles}
                                linkName={`/BadgeImageManager`}
                                linkState={clientEventInfo}
                                disabledBool={regTypeMappingProgress !== "Success" ? true : false}
                            />
                            <Card
                                dataImage={FieldOrdering}
                                header="Field Ordering"
                                content={`Arrange mapped fields in order you want them to be presented on your badges`}
                                styles={styles}
                                linkName={`/BadgeTemplateFieldOrdering`}
                                linkState={clientEventInfo}
                                disabledBool={regTypeMappingProgress !== "Success" ? true : false}
                            />
                        </div>
                    </div>
            :
                <LoadingSpinner />
        }
        </>
    );
}

export default EventBadgesStatusManager;